import { Flex } from "@similarweb/ui-components/foundations";

import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { swSettings } from "app/common/services/swSettings";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context";
import { useTranslation } from "pro-features/components/WithTranslation";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import type { MetricKey } from "../types";

import { HighLevelMetricsComparisonChartLegends } from "./HighLevelMetricsComparisonChartLegends";
import { HighLevelMetricsComparisonChartSwitcher } from "./HighLevelMetricsComparisonChartSwitcher";
import { exportChartAsPNG } from "./utils/chartExport";
import type { GranularityOption } from "./utils/granularityUtils";
import { getAvailableGranularityOptions, getCurrentGranularityIndex } from "./utils/granularityUtils";

const excelEndpoint = "api/Market/TrafficAndEngagement/OverviewIndustryCompare/Excel";

type HighLevelMetricsComparisonChartHeaderProps = {
    selectedMetric: MetricKey;
    timeGranularity: granularityItem;
    legendItems: ILegendItem[];
    chartElement: HTMLElement | null;
    onLegendToggle: (legendItem: ILegendItem) => void;
    onGranularityChange: (selectedIndex: number) => void;
    isPeriodOverPeriod?: boolean;
};

const getExcelLink = (categories: ICategoryComparison[], timeGranularity: GranularityOption) => {
    const { duration, country, includeSubdomains = true } = proNavigator.getParams();
    const last28daysParam =
        duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {};

    const { from, to } = DurationService.getDurationData(duration).forAPI;

    const queryParamsExtended = {
        ...last28daysParam,
        country,
        from,
        to,
        timeGranularity: timeGranularity.granularity.value,
        categories: categories.map(({ category }) => category.id),
        keys: categories.map(({ category }) => category.forApi),
        includeSubdomains,
    };

    const fetcherService = DefaultFetchService.getInstance();
    return `${excelEndpoint}?${fetcherService.requestParams(queryParamsExtended)}`;
};

export const HighLevelMetricsComparisonChartHeader = ({
    selectedMetric,
    timeGranularity,
    legendItems,
    chartElement,
    onLegendToggle,
    onGranularityChange,
    isPeriodOverPeriod = false,
}: HighLevelMetricsComparisonChartHeaderProps) => {
    const translation = useTranslation();
    const availableGranularityOptions = getAvailableGranularityOptions(selectedMetric, translation);
    const currentGranularityIndex = getCurrentGranularityIndex(timeGranularity, availableGranularityOptions);
    const currentGranularity = availableGranularityOptions[currentGranularityIndex];

    const switcherValues = availableGranularityOptions.map((option) => ({
        label: option.label,
        disabled: isPeriodOverPeriod ? option.granularity.value !== toggleItems.Monthly.value : option.disabled,
    }));

    const { categories } = useCategoriesComparison();

    const excelLink = getExcelLink(categories, currentGranularity);
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const handleDownloadPNG = async () => {
        if (!chartElement) {
            return;
        }

        await exportChartAsPNG(chartElement, selectedMetric, timeGranularity.value);
    };

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            margin="0 0 24px 0"
            height="40px"
        >
            <HighLevelMetricsComparisonChartLegends
                legendItems={legendItems}
                toggleLegendItem={onLegendToggle}
            />
            <Flex
                gap="8px"
                alignItems="center"
            >
                <HighLevelMetricsComparisonChartSwitcher
                    values={switcherValues}
                    selectedIndex={currentGranularityIndex}
                    onChange={onGranularityChange}
                />
                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={excelLink}
                    excelLocked={!downloadExcelPermitted}
                />
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={handleDownloadPNG}
                />
            </Flex>
        </Flex>
    );
};
